import React, { useId } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Plus, Minus, Users, CaretDown } from "@phosphor-icons/react";
import { useSettings } from "@/context/SettingsContext";
import { pick } from "@/lib/languages";
import { companionL, searchFormL } from "@/i18n/search";
import { dsL } from "@/i18n/ds";
import { needsCompanion, MAX_CHILDREN, MAX_TRAVELLERS } from "@/lib/flightSearchContract";
import { MAX_STAY_GUESTS } from "@/lib/staySearch";
import { isNativeApp } from "@/lib/nativeApp";

// The travellers control of the search form: one field, "Voyageurs / 2 adultes
// · 1 chambre", that opens a popover of steppers. Stepper is only ever used
// here, so it lives here. Counts and their setters belong to SearchWidget.

export function GuestsRow({ adults, children, rooms, setAdults, setChildren, setRooms, showRooms = true, testId, childAges, setChildAges, accompanied = false, setAccompanied,
  open, onOpenChange, companionErrorId, phoneMenu = false }) {
  const { t, lang } = useSettings();
  const ageId = useId();
  const isFlight = Array.isArray(childAges);
  // A flight may carry children without an adult on the same booking (a child
  // under 12 then needs the customer's word that an adult travels too, here and
  // again on the passenger form), but never nobody: the last child stays while
  // there is no adult.
  const minAdults = isFlight && children > 0 ? 0 : 1;
  const minChildren = isFlight && adults === 0 ? 1 : 0;
  const companion = pick(companionL, lang);
  const words = pick(searchFormL, lang);
  const count = pick(dsL, lang).count;
  // A flight caps the party (search contract); a stay at the size its search
  // and its property page read (lib/staySearch).
  const full = isFlight ? adults + children >= MAX_TRAVELLERS : adults + children >= MAX_STAY_GUESTS;
  // "2 adultes, 1 enfant · 1 chambre": the party in one line, as it will be priced.
  const party = [adults > 0 ? count.adults(adults) : null, children > 0 ? count.children(children) : null].filter(Boolean).join(", ");
  const summary = [party, showRooms ? count.rooms(rooms) : null].filter(Boolean).join(" · ");
  return (
    <Popover {...(open === undefined ? {} : { open, onOpenChange })}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testId} className="tk-search-guests w-full min-w-0 flex items-center gap-2 min-h-[56px] rounded-lg bg-white border border-touki px-3 hover:border-touki-black transition-colors duration-fast text-start">
          <Users size={18} weight={isNativeApp() ? "regular" : "bold"} className="shrink-0 text-touki-muted" aria-hidden="true" />
          <span className={phoneMenu ? "tk-search-guests-text min-w-0" : "min-w-0"}>
            <span className="block text-label-sm font-medium text-touki-muted">{words.travellers}</span>
            <span className="block truncate text-body-md font-semibold text-touki-black tabular-nums">{summary}</span>
          </span>
          {/* The flights search on a phone shows the party as a head count in
              its row of menus (index.css); the words above stay its name. */}
          {phoneMenu && <span className="tk-search-guests-count" aria-hidden="true">{adults + children}</span>}
          {phoneMenu && <CaretDown size={14} weight="bold" className="tk-search-menu-caret" aria-hidden="true" />}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start">
        <Stepper label={`${t("search.adults")}${isFlight ? " (18+)" : ""}`} value={adults} onDec={() => setAdults(Math.max(minAdults, adults - 1))} onInc={() => setAdults(adults + 1)} testId="adults" min={minAdults} max={full ? adults : undefined} />
        <Stepper label={t("search.children")} value={children} onDec={() => setChildren(Math.max(minChildren, children - 1))} onInc={() => setChildren(children + 1)} testId="children" min={minChildren} max={full || (isFlight && children >= MAX_CHILDREN) ? children : undefined} />
        {isFlight && childAges.map((age, index) => (
          <div key={index} className="py-2">
            <label htmlFor={`${ageId}-${index}`} className="block text-sm font-medium">
              {words.childAge} {index + 1}
            </label>
            <input id={`${ageId}-${index}`} data-testid={`flight-child-age-${index}`}
              type="number" min="2" max="17" step="1" required value={age}
              aria-describedby={`${ageId}-hint`} aria-invalid={age === "" || !/^\d+$/.test(String(age)) || Number(age) < 2 || Number(age) > 17}
              onChange={event => setChildAges(childAges.map((value, i) => i === index ? event.target.value : value))}
              className="w-full min-h-11 border border-touki rounded-md px-3" />
          </div>
        ))}
        {isFlight && childAges.length > 0 && <p id={`${ageId}-hint`} className="text-xs text-touki-muted">{words.ageHint}</p>}
        {isFlight && needsCompanion(adults, childAges) && (
          <div className="mt-2 rounded-md border border-touki bg-touki-light p-3" data-testid="flight-companion">
            <p className="text-xs font-semibold text-touki-black">{companion.rule}</p>
            <label className="mt-2 flex min-h-11 cursor-pointer items-start gap-2 text-sm text-touki-black">
              <input type="checkbox" data-testid="flight-accompanied" checked={accompanied} onChange={(e) => setAccompanied?.(e.target.checked)}
                aria-invalid={Boolean(companionErrorId) && !accompanied} aria-describedby={companionErrorId || undefined}
                className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{companion.confirmAll}</span>
            </label>
          </div>
        )}
        {showRooms && <Stepper label={t("search.rooms")} value={rooms} onDec={() => setRooms(Math.max(1, rooms - 1))} onInc={() => setRooms(rooms + 1)} testId="rooms" min={1} />}
      </PopoverContent>
    </Popover>
  );
}

export function Stepper({ label, value, onDec, onInc, testId, min = 0, max }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b last:border-b-0 border-touki">
      <div className="text-sm font-medium">{label}</div>
      <div className="flex items-center gap-3">
        <button type="button" aria-label={`− ${label}`} onClick={onDec} data-testid={`step-dec-${testId}`} className="h-11 w-11 rounded-md border border-touki hover:border-touki-black inline-flex items-center justify-center disabled:opacity-40" disabled={value <= min}><Minus size={14} weight="bold" /></button>
        <span className="min-w-[20px] text-center text-sm font-semibold" data-testid={`step-val-${testId}`}>{value}</span>
        <button type="button" aria-label={`+ ${label}`} onClick={onInc} data-testid={`step-inc-${testId}`} className="h-11 w-11 rounded-md border border-touki hover:border-touki-black inline-flex items-center justify-center disabled:opacity-40" disabled={max !== undefined && value >= max}><Plus size={14} weight="bold" /></button>
      </div>
    </div>
  );
}
